import React, { FC } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as ExpandIcon } from 'studio_react/svg/common/expand.svg';
import { ReactComponent as PauseIcon } from 'studio_react/svg/common/pause.svg';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import { ReactComponent as VolumeIcon1 } from 'studio_react/svg/common/volume-1.svg';
import { ReactComponent as VolumeIcon2 } from 'studio_react/svg/common/volume-2.svg';
import { ReactComponent as VolumeIconX } from 'studio_react/svg/common/volume-x.svg';
import { ReactComponent as VolumeIcon0 } from 'studio_react/svg/common/volume.svg';
import { ReactComponent as SettingsIcon } from 'studio_react/svg/galleries/settings.svg';
import { useTranslate } from 'ts/common/hooks';
import ControlButton from './ControlButton';
import VideoSettings from './VideoSettings';

const formatTime = (s: number): string => {
    if (!isFinite(s) || isNaN(s)) {
        return '0:00';
    }

    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);

    return `${m}:${sec.toString().padStart(2, '0')}`;
};

interface Props {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
    isFullscreen: boolean;
    showSettings: boolean;
    playbackSpeed: number;
    quality: string;
    onPlayPause: () => void;
    onSeek: (time: number) => void;
    onToggleMute: () => void;
    onToggleFullscreen: () => void;
    onToggleSettings: () => void;
    onSpeedChange: (speed: number) => void;
    onQualityChange: (quality: string) => void;
}

const VideoControls: FC<Props> = ({
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isFullscreen,
    showSettings,
    playbackSpeed,
    quality,
    onPlayPause,
    onSeek,
    onToggleMute,
    onToggleFullscreen,
    onToggleSettings,
    onSpeedChange,
    onQualityChange
}) => {
    const t = useTranslate('studio.common.videoPlayer');
    const fillPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    const VolumeIcon =
        isMuted || volume === 0
            ? VolumeIconX
            : volume <= 0.33
            ? VolumeIcon0
            : volume <= 0.66
            ? VolumeIcon1
            : VolumeIcon2;

    return (
        <Box position="absolute" bottom={0} left={0} right={0} zIndex={2}>
            {/* Gradient for readability — behind everything */}
            <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="120px"
                background="linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)"
                pointerEvents="none"
                zIndex={0}
            />

            {/* Settings + control bar stacked in column — no absolute gap */}
            {/* stopPropagation prevents clicks here from bubbling to the video click-to-play handler */}
            <Flex
                direction="column"
                position="relative"
                zIndex={1}
                px="15px"
                pb="17px"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                {/* Settings panel — flows above control bar, right-aligned */}
                {showSettings && (
                    <Box alignSelf="flex-end" mb="8px">
                        <VideoSettings
                            playbackSpeed={playbackSpeed}
                            quality={quality}
                            onSpeedChange={onSpeedChange}
                            onQualityChange={onQualityChange}
                        />
                    </Box>
                )}

                {/* Control bar */}
                <Flex h="40px" alignItems="center" gap="4px">
                    {/* Play / Pause — dark background, fixed 48×40 */}
                    <ControlButton
                        onClick={onPlayPause}
                        ariaLabel={isPlaying ? t('pause') : t('play')}
                        bg="rgba(0,0,0,0.6)"
                        w="48px"
                        h="40px"
                    >
                        {isPlaying ? (
                            <PauseIcon width={20} height={20} />
                        ) : (
                            <PlayIcon width={20} height={20} />
                        )}
                    </ControlButton>

                    {/* Timeline pill */}
                    <Flex
                        flex={1}
                        bg="rgba(0,0,0,0.6)"
                        px="10px"
                        py="4px"
                        rounded="4px"
                        gap="12px"
                        h="40px"
                        alignItems="center"
                    >
                        {/* Scrub bar */}
                        <Box
                            as="input"
                            type="range"
                            min={0}
                            max={duration || 1}
                            value={currentTime}
                            step={0.1}
                            aria-label={t('seek')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onSeek(Number(e.target.value))
                            }
                            flex={1}
                            minW={0}
                            style={{
                                background: `linear-gradient(to right, white ${fillPercent}%, rgba(255,255,255,0.3) ${fillPercent}%)`
                            }}
                            sx={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                height: '4px',
                                borderRadius: '2px',
                                outline: 'none',
                                cursor: 'pointer',
                                '&::-webkit-slider-thumb': {
                                    WebkitAppearance: 'none',
                                    appearance: 'none',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    background: 'white',
                                    cursor: 'pointer'
                                },
                                '&::-moz-range-thumb': {
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    background: 'white',
                                    cursor: 'pointer',
                                    border: 'none'
                                }
                            }}
                        />

                        {/* Time */}
                        <Text color="white" fontSize="14px" whiteSpace="nowrap" flexShrink={0}>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </Text>

                        {/* Volume */}
                        <ControlButton
                            onClick={onToggleMute}
                            ariaLabel={isMuted ? t('unmute') : t('mute')}
                        >
                            <VolumeIcon width={24} height={24} />
                        </ControlButton>

                        {/* Settings */}
                        <ControlButton onClick={onToggleSettings} ariaLabel={t('settings')}>
                            <SettingsIcon width={24} height={24} />
                        </ControlButton>

                        {/* Fullscreen — Figma expand icon */}
                        <ControlButton
                            onClick={onToggleFullscreen}
                            ariaLabel={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                            bg="rgba(0,0,0,0.6)"
                        >
                            <Box
                                w="24px"
                                h="24px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <ExpandIcon width={20} height={20} />
                            </Box>
                        </ControlButton>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

export default VideoControls;
